import React, { forwardRef, useState, useEffect, Fragment, MouseEvent, useImperativeHandle } from 'react';
import { InputSearch } from './inputSearch';
import * as imageUtil from '@umu/umu-image-handle';
import { If } from 'common/src/components';
import { IProps, IParams, MEMBER_ASSESSMENT_TYPE } from './interface';
import { memberMap } from './dataFlow';
import { ISearchUserParam, searchUserList, searchUserListV2, IUserInfo } from '../../services/userSearch';
import { getAvatarBackgroundColor, getAvatarWord } from '../@loadAvatar/loadAvatarUtil';
import './index.scss';

const IMAGE_WIDTH = 25; // 图片宽度
type ChildHandle = {
    clear: () => void;
};
export const UserSearch = forwardRef((props: IProps, ref: React.Ref<ChildHandle>) => {
    const { isUseV2, extraParams, style = {}, overlayClassName, offset } = props;
    const [selectList, setSelectList] = useState<IParams[]>([]);

    useEffect(() => {
        setSelectList([]);
    }, [props.isClear]);

    useEffect(() => {
        setSelectList(props.selectList || []);
    }, [props.selectList]);

    // 清空列表方法，用于外部调用
    const clearList = () => {
        setSelectList([]);
    };

    // 向外面暴露的方法
    useImperativeHandle(ref, () => ({
        clear: clearList,
    }));

    const getList = async (val: string) => {
        if (val === '') {
            return [];
        }

        let result: IUserInfo[] = [];
        const params: ISearchUserParam = {
            accounts: val,
            search_source: props.searchSource || '',
        };

        if (extraParams) {
            Object.assign(params, extraParams);
        }

        if (isUseV2) {
            result = await searchUserListV2(params);
        } else {
            result = await searchUserList(params);
        }

        return result.map((item) => {
            return {
                icon: item.avatar || '',
                title: item.user_name || item.account || item.phone || item.email,
                label: item.email,
                id: item.id,
                type: memberMap[item.account_type],
                account_type: item.account_type,
            };
        });
    };

    const renderChildren = (item: IParams) => {
        return <span>{'"' + item.title + '"' + (item.label && item.label !== '' ? '<' + item.label + '>' : '')}</span>;
    };

    const setData = (list: IParams[]) => {
        setSelectList(list);
        if (props.onChange) {
            const ids = [];
            for (let i = 0; i < list.length; i++) {
                ids.push(list[i].id);
            }
            props.onChange(ids, list);
        }
    };

    const getFilter = (id: string) => {
        const list = selectList.filter((_) => {
            return id === _.id;
        });
        return list.length > 0;
    };

    const onSelect = (item: IParams) => {
        if (!getFilter(item.id)) {
            const pre: IParams[] = [...selectList];
            pre.push(item);
            setData(pre);
        }
    };

    const closeClick = (count: number) => {
        if (props.disabled) {
            if (props.disabledCallBack) {
                props.disabledCallBack();
            }
            return;
        }
        const pre: IParams[] = [...selectList];
        pre.splice(count, 1);
        setData(pre);
    };

    const getDefaultIcon = (data: IParams) => {
        let result: JSX.Element | string = '';
        switch (data.type) {
            case MEMBER_ASSESSMENT_TYPE.DEPART:
                result = (
                    <div className="avatar-word">
                        <div className="icon-depart" />
                    </div>
                );
                break;
            case MEMBER_ASSESSMENT_TYPE.GROUP:
                result = (
                    <div className="avatar-word">
                        <div className="icon-group" />
                    </div>
                );
                break;
            case MEMBER_ASSESSMENT_TYPE.CLASS:
                result = (
                    <div className="avatar-word">
                        <div className="icon-class" />
                    </div>
                );
                break;
            case MEMBER_ASSESSMENT_TYPE.MEMBER:
                result = <div className="avatar-word">{getAvatarWord(data.title)}</div>;
                break;
            default:
                break;
        }
        return result;
    };
    const getBackgroundColor = (type: MEMBER_ASSESSMENT_TYPE, id: string, isHasIcon: boolean) => {
        let color = getAvatarBackgroundColor(id);
        if (isHasIcon) {
            return '';
        }
        if (type === MEMBER_ASSESSMENT_TYPE.GROUP) {
            color = '#07bbd4';
        }
        if (type === MEMBER_ASSESSMENT_TYPE.DEPART) {
            color = '#ffbd2c';
        }
        return color;
    };
    const onDivClick = (event: MouseEvent<HTMLSpanElement>) => {
        event.stopPropagation();
    };
    const renderData = (item: IParams, num: number) => {
        const isHasIcon = !!(item.icon && item.icon !== '');
        const cancelClick = () => {
            closeClick(num);
        };

        return (
            <span className="assessment-search-render-wrap" key={item.id} onClick={onDivClick}>
                <div className="img" style={{ backgroundColor: getBackgroundColor(item.type, item.id, isHasIcon) }}>
                    <If condition={isHasIcon}>
                        <img src={imageUtil.appendWidth(item.icon, IMAGE_WIDTH)} className="loaded" />
                    </If>
                    <If condition={!isHasIcon}>
                        <Fragment>{getDefaultIcon(item)}</Fragment>
                    </If>
                </div>
                <div className="title-search">
                    <span className="title-text" title={item.title}>
                        {item.title}
                    </span>
                    <i className={`icon-search-close${props.disabled ? '-disabled' : ''}`} onClick={cancelClick} />
                </div>
            </span>
        );
    };

    return (
        <InputSearch
            style={style}
            disabled={props.disabled}
            placeholder={props.placeholder}
            getList={getList}
            maxCount={props.limitMax}
            selectList={selectList}
            onSelect={onSelect}
            renderData={renderData}
            renderChildren={renderChildren}
            overlayClassName={overlayClassName}
            className="search-assessment-input"
            offset={offset}
        />
    );
});
